import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Currency } from '../types';
import { api, isApiConnected } from '../utils/apiClient';
import { mapCurrencyFromDb, getErrorMessage } from '../utils/dataMappers';
import { MOCK_CURRENCIES } from '../constants';

interface CurrencyContextType {
  currencies: Currency[];
  activeCurrency: Currency | null;
  setCurrency: (code: string) => void;
  formatPrice: (amountInUSD: number) => string;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currencies, setCurrencies] = useState<Currency[]>(MOCK_CURRENCIES);
  const [activeCurrency, setActiveCurrency] = useState<Currency | null>(() => {
    // Immediate hydration from localStorage to prevent flicker
    if (typeof window !== 'undefined') {
      try {
        const savedCode = localStorage.getItem('user_currency');
        if (savedCode) {
          // If it matches a mock, use it immediately
          const found = MOCK_CURRENCIES.find(c => c.code === savedCode);
          if (found) return found;
          
          // If it's something else like TZS, we create a temporary placeholder 
          // to avoid USD appearing then switching, if we know the symbol.
          // Native Intl can help us get a temporary symbol even if rate is unknown.
          const tempSymbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: savedCode })
            .formatToParts(0).find(p => p.type === 'currency')?.value || '$';
            
          return { 
            code: savedCode, 
            name: savedCode, 
            symbol: tempSymbol, 
            rate: 1, // Will be updated by fetch
            isBase: false, 
            status: 'Active', 
            lastUpdated: new Date().toISOString() 
          } as Currency;
        }

        // Try browser detection during initialization (non-blocking)
        const browserCurrency = Intl.NumberFormat().resolvedOptions().currency;
        if (browserCurrency) {
          const found = MOCK_CURRENCIES.find(c => c.code === browserCurrency);
          if (found) return found;
        }
      } catch (e) {
        console.warn("Currency hydration failed", e);
      }
    }
    return MOCK_CURRENCIES[0] || null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCurrencies = async () => {
    // If not configured, don't even try to fetch - use mocks immediately
    if (!isApiConnected) {
      setCurrencies(MOCK_CURRENCIES);
      return MOCK_CURRENCIES;
    }

    try {
      const { data, error } = await api
        .from('currencies')
        .select('*')
        .eq('status', 'Active');

      if (error) throw error;

      if (Array.isArray(data) && data.length > 0) {
        // Double-safety: filter by Active status after mapping
        const mapped = data.map(mapCurrencyFromDb).filter((c: any) => c.status === 'Active');
        if (mapped.length > 0) {
          setCurrencies(mapped);
          return mapped;
        }
      }

      setCurrencies(MOCK_CURRENCIES);
      return MOCK_CURRENCIES;
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.warn("Failed to fetch currencies from cloud, using mocks:", msg);
      setCurrencies(MOCK_CURRENCIES);
      return MOCK_CURRENCIES;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const fetched = await fetchCurrencies();

      // 1. Check LocalStorage
      const saved = localStorage.getItem('user_currency');
      const base = fetched.find(c => c.isBase) || fetched[0] || MOCK_CURRENCIES[0];

      if (saved) {
        const found = fetched.find(c => c.code === saved);
        if (found) {
          setActiveCurrency(found);
          setIsLoading(false);
          return;
        }
      }

      // 2. Geolocation/Browser Locale Detection
      try {
        const browserCurrency = Intl.NumberFormat().resolvedOptions().currency;
        const matched = fetched.find(c => c.code === browserCurrency);
        if (matched) {
          setActiveCurrency(matched);
        } else {
          setActiveCurrency(base);
        }
      } catch (e) {
        setActiveCurrency(base);
      }

      setIsLoading(false);
    };

    initialize();
  }, []);

  const setCurrency = (code: string) => {
    const found = currencies.find(c => c.code === code);
    if (found) {
      setActiveCurrency(found);
      localStorage.setItem('user_currency', code);
    }
  };

  const formatPrice = useCallback((amountInUSD: number) => {
    // Robust check for finite number to prevent RangeError in Intl.NumberFormat
    const safeAmount = (amountInUSD !== null && amountInUSD !== undefined && isFinite(amountInUSD)) ? amountInUSD : 0;

    if (!activeCurrency) return `$${safeAmount.toLocaleString()}`;

    // Convert
    const convertedAmount = safeAmount * activeCurrency.rate;

    // Format using native Internationalization API for correct symbols and decimals
    try {
      // Robust detection of decimal usage
      // TZS usually has 0, USD has 2, EUR has 2
      const hasDecimals = !['TZS', 'UGX', 'RWF', 'BIF', 'JPY', 'KRW'].includes(activeCurrency.code);
      
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency.code,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0
      }).format(convertedAmount);
    } catch (e) {
      // Fallback
      const symbol = activeCurrency.symbol || '$';
      return `${symbol}${convertedAmount.toLocaleString(undefined, { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      })}`;
    }
  }, [activeCurrency]);

  return (
    <div className="currency-context-wrapper">
      <CurrencyContext.Provider value={{
        currencies,
        activeCurrency,
        setCurrency,
        formatPrice,
        isLoading
      }}>
        {children}
      </CurrencyContext.Provider>
    </div>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

